import React, { Fragment, useEffect, useState } from 'react';

import Button from 'components/Buttons/Button';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeaderSubtitle';
import RadioButton from 'components/RadioButton';
import Shimmer from 'components/Shimmer';
import { ReactComponent as TicketIcon } from 'assets/images/private-fare.svg';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { UpSellInclusion, UpSellInclusionType, UpSellOption } from 'models/upsell';
import PolicyTag from 'screens/components/UpsellOptions/components/PolicyTag';
import {
  getInclusions,
  findInclusionByCode,
  getType,
} from 'screens/components/UpsellOptions/components/UpsellDesktop/util';
import { getFormattedPrice } from 'utils/currency';
import { firstCapitalize } from 'utils/string';

import { Container, Table, YesIcon, NoIcon, Icon, Text, Price, ButtonContainer, PrivateFareIconContainer, PrivateFareMessage } from './styles';
import { ThemeType } from 'theme/theme';
import { useTheme } from 'styled-components';

interface Props {
  onClose: () => void;
  onSelect: (optionId: number) => void;
  onChange: (optionId: number) => void;
  options: UpSellOption[];
  loading: boolean;
}

const Item = ({ type, title, iidx }: { type: UpSellInclusionType; iidx: number; title?: string }) => {
  const { t } = useStoreTranslation();
  return type === UpSellInclusionType.AtCharge ? (
    <Fragment>
      <Text>{t('upsellFares.atCharge')}</Text>
    </Fragment>
  ) : type === UpSellInclusionType.Included ? (
    <Fragment>
      <Icon>
        <YesIcon />
      </Icon>
      {title && <Text className={`upsell-cell-text cell-${iidx}-cell`}>{firstCapitalize(title)}</Text>}
    </Fragment>
  ) : (
    <Icon>
      <NoIcon />
    </Icon>
  );
};

const UpSellDesktop = ({ onClose, onSelect, onChange, options, loading = true }: Props) => {
  const theme = useTheme() as ThemeType;
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined, currencyCode: string) => {
    const { formatted } = getFormattedPrice({ value, currency: currencyCode, t });
    return formatted;
  };
  const [inclusions, setInclusions] = useState<UpSellInclusion[]>([]);
  const [selected, setSelected] = useState('');

  const handleChange = (value) => {
    setSelected(value);
    onChange(Number(value));
  };

  useEffect(() => {
    if (selected === '' && options.length > 0) setSelected(String(options[0].optionId));
  }, [selected, options]);

  useEffect(() => {
    if (options && options.length > 0) setInclusions(getInclusions(options));
  }, [options]);

  const getTitle = (option: UpSellOption, code: string): string => {
    const data = findInclusionByCode(option, code);
    return data?.type === UpSellInclusionType.Included
      ? data.title && data.title !== ''
        ? data.title
        : data.subTitle && data.subTitle !== ''
        ? data.subTitle
        : ''
      : '';
  };

  const renderCell = (o, idx, inc, iidx) => (
    <Table.Item key={`${idx}-${iidx}-${inc.code}`} className={`upsell-cell cell-${iidx}-cell`}>
      <>
        <Item iidx={iidx} type={getType(o, inc.code) as UpSellInclusionType} title={getTitle(o, inc.code)} />
      </>
    </Table.Item>
  );

  const renderColumn = (o, idx) => (
    <Table.Column key={idx}>
      <Table.Item key={`${idx}-title`} className="title">
        {o.families[0].name.toUpperCase()}
      </Table.Item>
      {inclusions.map((inc, iidx) => renderCell(o, idx, inc, iidx))}
      <Table.Item className="footer price-footer" $hasAlternativePrice={true}>
        <Table.Div>
          <PolicyTag isPreApproved={o.isPreApproved === true ? true : false} breakRules={o.breakRulesMessage || []} />
        </Table.Div>
        <Table.Div>
          <RadioButton value={String(o.optionId)} optionValue={selected} handleChangeValue={handleChange} />
          <Price>{formattedPrice(o.amount.fare + o.amount.taxes, o.amount.currencyCode)}
            {o.amountAlternative && (
            <Table.DivAlternative>
              {t('alternativePrice.or')}
              <Price>
                {formattedPrice(o.amountAlternative.fare + o.amountAlternative.taxes, o.amountAlternative.currencyCode)}
              </Price>
            </Table.DivAlternative>
          )}
          </Price>
        </Table.Div>
        <Table.Div>
          {o.hasPrivateFare && (
          <PrivateFareMessage>
          <PrivateFareIconContainer>
            <TicketIcon stroke={theme.primaryBackgroundColor} />
          </PrivateFareIconContainer>
          {t('flightDetails.privateFare')}
        </PrivateFareMessage>
        )} 
        {!o.hasPrivateFare && (
          <><br /></>
        )}
        </Table.Div>
        
      </Table.Item>
    </Table.Column>
  );

  const renderCellSubTitle = (inc) => {
    const title = inc.title ? firstCapitalize(inc.title.replace('<br/>', '').replace('<br>', '')) : '';
    const subtitle = inc.subTitle ? firstCapitalize(inc.subTitle) : '';
    const label =
      title && subtitle ? `${title}. ${subtitle}` : title && !subtitle ? title : !title && subtitle ? subtitle : '';
    return label && <Table.ItemSmall>{label}</Table.ItemSmall>;
  };

  const fixUpsellRowSizes = () => {
    const columns = document.getElementsByClassName('upsell-column');
    const cells = document.getElementsByClassName('upsell-cell');
    const cellsTexts = document.getElementsByClassName('upsell-cell-text');

    for (let index = 0; index < columns.length; index++) {
      let higherHeight = 0;
      for (let index_text = 0; index_text < cellsTexts.length; index_text++) {
        if (cellsTexts[index_text].className.indexOf(`cell-${index}-cell`) !== -1) {
          const currentHeight = cellsTexts[index_text].clientHeight;

          if (higherHeight < currentHeight) {
            higherHeight = currentHeight;
          }
        }
      }

      for (let index_cell = 0; index_cell < cells.length; index_cell++) {
        if (cells[index_cell].className.indexOf(`cell-${index}-cell`) !== -1) {
          (cells[index_cell] as HTMLElement).style.maxHeight = `${higherHeight + 45}px`;
          (cells[index_cell] as HTMLElement).style.height = `${higherHeight + 42}px`;
          (cells[index_cell] as HTMLElement).style.padding = '6px 8px';
        }
      }
    }

    const blankFooter = document.getElementsByClassName('blank-footer');
    const priceFooter = document.getElementsByClassName('price-footer');

    if (blankFooter !== undefined && blankFooter.length > 0 && priceFooter !== undefined && priceFooter.length > 0) {
      (blankFooter[0] as unknown as HTMLElement).style.height = `${(priceFooter[0] as HTMLElement).offsetHeight}px`;
    }
  };

  useEffect(() => {
    if (!loading) {
      fixUpsellRowSizes();
    }
  }, [loading]);

  return (
    <Fragment>
      {loading ? (
        <Fragment>
          <ModalHeader
            onClose={onClose}
            title={<Shimmer>Customize your rate</Shimmer>}
            subtitle={<Shimmer>Select the rate that best suits you on your trip.</Shimmer>}
          />
          <Loader fullScreen={false} />
        </Fragment>
      ) : (
        options &&
        options?.length > 0 && (
          <Fragment>
            <ModalHeader onClose={onClose} title={t('upsellFares.title')} subtitle={t('upsellFares.subtitle')} />
            <Container>
              <Table>
                <Table.Container>
                  <Table.Column className="title">
                    <Table.Item className="title"></Table.Item>
                    {inclusions.map((inc, iidx) => (
                      <Table.Item className={`head upsell-cell cell-${iidx}-cell  upsell-column`} key={`head-${iidx}`}>
                        {inc.rowName && firstCapitalize(inc.rowName)}
                        {renderCellSubTitle(inc)}
                      </Table.Item>
                    ))}
                    <Table.Item className="footer blank-footer"></Table.Item>
                  </Table.Column>
                  {options.map((o, idx) => renderColumn(o, idx))}
                </Table.Container>
              </Table>

              <ButtonContainer>
                <Button onClick={() => onSelect(Number(selected))}>{t('upsellFares.button')}</Button>
              </ButtonContainer>
            </Container>
          </Fragment>
        )
      )}
    </Fragment>
  );
};
export default UpSellDesktop;
